import React from 'react';
import _ from 'underscore';
import invariant from 'invariant';

const displayName = Component => Component.displayName || Component.name;

const factory = function(Component) {
    if (process.env.NODE_ENV !== 'production') {
        invariant(displayName, 'Missing displayName');
    }
    const propStore = (function() {
        let callback;
        let owner;

        return {
            set owner(newOwner) {
                if (newOwner) {
                    if (process.env.NODE_ENV !== 'production') {
                        invariant(
                            !owner,
                            'Proxy can only have one owner at a time'
                        );
                    } else {
                        invariant(!owner);
                    }
                }
                owner = newOwner;
                if (callback) {
                    callback();
                }
            },
            get props() {
                return owner && owner.props;
            },
            notify() {
                if (callback) {
                    callback();
                }
            },
            on(cb) {
                if (process.env.NODE_ENV !== 'production') {
                    invariant(
                        callback == null,
                        'A callback is already registered'
                    );
                } else {
                    invariant(callback == null);
                }
                callback = cb;
            },
            off(cb) {
                if (process.env.NODE_ENV !== 'production') {
                    invariant(cb === callback, '');
                } else {
                    invariant(cb === callback);
                }
                callback = null;
            },
        };
    })();

    class Wrapped extends React.Component {
        static displayName = `Wrapped(${displayName(Component)})`;
        state = { childProps: propStore.props };

        componentDidMount() {
            propStore.on(this.updateState);
        }

        componentWillUnmount() {
            propStore.off(this.updateState);
        }

        updateState = () => {
            this.setState({
                // Reset to the defaults if the props are removed.
                childProps: propStore.props,
            });
        };

        render() {
            const props = _.extend({}, this.state.childProps || this.props, {
                defaultProps: this.props,
            });
            return React.createElement(Component, props);
        }
    }

    Wrapped.Proxy = class extends React.Component {
        static displayName = `${displayName(Component)}Proxy`;

        componentDidMount() {
            propStore.owner = this;
        }

        componentDidUpdate() {
            propStore.notify();
        }

        componentWillUnmount() {
            propStore.owner = undefined;
        }

        render() {
            // At least with React 0.13, returning null here causes React to replace
            // the `<noscript` placeholder every time.
            // TODO: Try null here when we upgrade to React 15
            return <span />;
        }
    };

    return Wrapped;
};

export default factory;



// WEBPACK FOOTER //
// ./src/components/proxy_factory.jsx